import { MEGASTRUCTURE, type ShipClassId } from '@sd/content';
import { t, type Locale } from '@sd/i18n';
import {
  fleetTicksPerHex,
  hexKey,
  hullShare,
  exploredKeys,
  seenFleets,
  visibleHexes,
  visionInput,
  type FleetState,
  type GameState,
  type Hex,
  type Planet,
  type StarSystem,
} from '@sd/sim';
import {
  BoxGeometry,
  Color,
  DoubleSide,
  Group,
  InstancedMesh,
  LineBasicMaterial,
  Line,
  LineDashedMaterial,
  LineLoop,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  OctahedronGeometry,
  Quaternion,
  RingGeometry,
  TetrahedronGeometry,
  TorusGeometry,
  Vector3,
  BufferGeometry,
  Float32BufferAttribute,
  type Material,
} from 'three';
import { CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';
import { LAYER_NO_OUTLINE } from '../outline';
import { PIRATE_COLOR, PLANET_COLORS, seatColorHex } from '../palette';
import { HEX_SIZE, bodyTop, hexToWorld } from '../systemView';
import {
  constructorGeometry,
  landerGeometry,
  corvetteGeometry,
  cruiserGeometry,
  derrickGeometry,
  frigateGeometry,
  linerGeometry,
  mineGeometry,
  raiderGeometry,
  rockGeometry,
} from './models';
import { clock, outpostName } from '../ui/format';
import { progressItems } from './progress';

/**
 * What moves and changes on the map during a match (#55): fleets and
 * caravans, outposts and stations, pirates, debris, battles and sieges,
 * the swarm, the crisis sector and joint project sites. The static system
 * (tiles, star, planets, clouds) stays in systemView.
 *
 * Many similar objects share one InstancedMesh each, so 300 ships cost a
 * handful of draw calls. Fleets glide between snapshots instead of jumping
 * from tick to tick.
 */

/** Warm colours belong to the star (docs/design/ui-style.md). */
const HEAT = '#ffb347';
const BATTLE = '#ff8080';
const DOWN = '#59607a';
/** Found asteroids, the colour of the map's own (palette PLANET_COLORS.asteroids). */
const ASTEROID = PLANET_COLORS.asteroids;
/** Camera distance below which outposts and stations show their names. */
const NAMES_DISTANCE = 42;
/** ...and fleets their badges, a little further out (#111). */
const BADGES_DISTANCE = 60;
/** How long a bolt flies, s, and how each class fires (#117). */
const BOLT_SECONDS = 0.6;
const BOLTS: Partial<Record<ShipClassId, { count: number; size: number }>> = {
  corvette: { count: 1, size: 0.8 },
  raider: { count: 1, size: 0.8 },
  frigate: { count: 2, size: 1 },
  cruiser: { count: 1, size: 1.8 },
};
const FORWARD = new Vector3(0, 0, 1);

/** A self-lit material: bolts glow whatever the light. */
const glow = () => new MeshBasicMaterial({ color: '#ffffff' });

/** A fleet is drawn as the first of these it holds (#111); explorers alone keep their crystal. */
const FLAGSHIP_ORDER = [
  'cruiser',
  'frigate',
  'raider',
  'corvette',
  'tradeLiner',
  'lander',
  'transport',
] as const;

const flat = (color = '#ffffff') =>
  new MeshStandardMaterial({ color, flatShading: true, roughness: 0.8, metalness: 0.05 });

/** A pool of instances of one shape; `count` is set every update. */
class Instances {
  readonly mesh: InstancedMesh;
  private used = 0;
  private readonly matrix = new Matrix4();
  private readonly color = new Color();

  constructor(geometry: BufferGeometry, capacity: number, material: Material = flat()) {
    this.mesh = new InstancedMesh(geometry, material, capacity);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
  }

  begin() {
    this.used = 0;
  }

  add(position: Vector3, color: string, scale = 1, rotation = new Quaternion()) {
    if (this.used >= this.mesh.instanceMatrix.count) return;
    this.matrix.compose(position, rotation, new Vector3(scale, scale, scale));
    this.mesh.setMatrixAt(this.used, this.matrix);
    this.mesh.setColorAt(this.used, this.color.set(color));
    this.used++;
  }

  end() {
    this.mesh.count = this.used;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  dispose() {
    this.mesh.geometry.dispose();
    (this.mesh.material as Material).dispose();
  }
}

/**
 * Fleets stand around the edge of their hex, not in its middle, so a
 * planet or another fleet on the same hex never hides them: slot i of a
 * hex sits at 30° + 60°·i, inside the tile.
 */
function slotOffset(slot: number): Vector3 {
  const a = ((30 + 60 * (slot % 6)) * Math.PI) / 180;
  const r = 0.62 * HEX_SIZE;
  return new Vector3(r * Math.cos(a), 0, r * Math.sin(a));
}

/** Where a fleet is between two hexes, from its progress along the path. */
function fleetPosition(f: FleetState, slot: number): { at: Vector3; heading: number | null } {
  const offset = slotOffset(slot);
  const from = hexToWorld(f.hex, 0.9).add(offset);
  const next = f.path[0];
  if (!next) return { at: from, heading: null };
  const to = hexToWorld(next, 0.9).add(offset);
  const share = Math.min(1, f.progress / fleetTicksPerHex(f));
  return { at: from.clone().lerp(to, share), heading: Math.atan2(to.x - from.x, to.z - from.z) };
}

const UP = new Vector3(0, 1, 0);

function hexOutline(h: Hex, color: string, y = 0.04, size = 1): LineLoop {
  const center = hexToWorld(h, y);
  const points: number[] = [];
  for (let i = 0; i < 6; i++) {
    const a = ((60 * i - 30) * Math.PI) / 180;
    const r = HEX_SIZE * size;
    points.push(center.x + r * Math.cos(a), center.y, center.z + r * Math.sin(a));
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(points, 3));
  const line = new LineLoop(geometry, new LineBasicMaterial({ color }));
  line.layers.set(LAYER_NO_OUTLINE);
  return line;
}

function label(text: string, className: string): CSS2DObject {
  const element = document.createElement('div');
  element.className = className;
  element.textContent = text;
  return new CSS2DObject(element);
}

export class LiveLayer {
  readonly root = new Group();
  /**
   * Ships (#111): a fleet is drawn as its biggest ship, each class its own
   * silhouette; explorers alone keep their spinning crystal.
   */
  private readonly hulls: Readonly<Record<Exclude<ShipClassId, 'explorer'>, Instances>> = {
    corvette: new Instances(corvetteGeometry(), 512),
    frigate: new Instances(frigateGeometry(), 512),
    cruiser: new Instances(cruiserGeometry(), 256),
    raider: new Instances(raiderGeometry(), 256),
    tradeLiner: new Instances(linerGeometry(), 256),
    transport: new Instances(constructorGeometry(), 256),
    lander: new Instances(landerGeometry(), 256),
  };
  private readonly explorers = new Instances(new OctahedronGeometry(0.34), 256);
  /** Over every fleet in sight (#111): its ships and hull, its owner's colour. */
  private readonly badges = new Group();
  private readonly badgeOf = new Map<
    number,
    { object: CSS2DObject; count: HTMLElement; hull: HTMLElement; key: string }
  >();
  /** Fleets picked together with a drag (#111). */
  private group: ReadonlySet<number> = new Set();
  /** The player's fleets hit lately (#156): a red «!» over them. */
  private hit: ReadonlySet<number> = new Set();
  /** Laser bolts in flight (#117): from a shooter's hex to its target's, gone on arrival. */
  private readonly bolts = new Instances(new BoxGeometry(0.09, 0.09, 0.7), 512, glow());
  private flying: { from: Vector3; to: Vector3; start: number; color: string; size: number }[] = [];
  private now = 0;
  /** Extraction sites (#103): a derrick on gas giants, a mine on rock. */
  private readonly derricks = new Instances(derrickGeometry(), 128);
  private readonly mines = new Instances(mineGeometry(), 128);
  /** Asteroids found in dust clouds: not on the static map, so drawn here. */
  private readonly rocks = new Instances(rockGeometry(), 256);
  private readonly stations = new Instances(new OctahedronGeometry(0.4, 0), 32);
  /** Bodies by hex, to stand extraction sites on top of them. */
  private bodies: { system: StarSystem; byHex: Map<string, Planet> } | null = null;
  private readonly debris = new Instances(new TetrahedronGeometry(0.16), 256);
  private readonly battles: Instances;
  private readonly scans: Instances;
  private readonly swarm = new Instances(
    new TorusGeometry(0.62, 0.07, 4, 18).rotateX(Math.PI / 2),
    32,
  );
  private readonly overlays = new Group();
  /** Where each fleet is drawn now; it glides towards where the simulation puts it. */
  private readonly shown = new Map<number, Vector3>();
  private readonly targets = new Map<number, { at: Vector3; heading: number | null }>();
  private state: GameState | null = null;
  /** Fleets the player sees this snapshot. */
  private seen: readonly FleetState[] = [];
  private fogSignature = '';
  /** What the player has explored (#97), rebuilt only when it grows. */
  private explored: ReadonlySet<string> | null = null;
  private exploredFrom: readonly number[] | undefined;
  /** What the player sees right now: battles elsewhere stay unknown (#97). */
  private visible: ReadonlySet<string> | null = null;
  private overlayKey = '';
  /** The hex under the cursor and the selection: thin outlines that move, never rebuilt. */
  private readonly hover = hexOutline({ q: 0, r: 0 }, '#e6eaf5', 0.06);
  /** Larger than the tile, so it reads beside a capital's own ring. */
  private readonly picked = hexOutline({ q: 0, r: 0 }, '#ffffff', 0.07, 1.12);
  private selectedFleet: number | null = null;
  /** The selected fleet's route, redrawn when it changes. */
  private route: Line | null = null;
  private routeKey = '';
  /** Names of outposts and stations, shown only close up (semantic zoom). */
  private readonly names = new Group();
  private namesKey = '';
  /** Timers over scans and construction (#96), kept by id so a bar only changes width. */
  private readonly progress = new Group();
  private readonly bars = new Map<
    string,
    { object: CSS2DObject; fill: HTMLElement; text: HTMLElement }
  >();

  constructor(
    private readonly me: string,
    private readonly locale: Locale,
    private readonly mapRadius: number,
    /** Tints the tiles of the sector a crisis will strike (systemView). */
    private readonly tintSector: (sector: number | null | undefined) => void,
    /**
     * Dims the tiles the player does not see (#33, systemView); without it —
     * the render benchmark — every fleet is shown.
     */
    private readonly setFog?: (
      visible: ReadonlySet<string> | null,
      explored: ReadonlySet<string> | null,
    ) => void,
  ) {
    const ring = (inner: number, outer: number, color: string, opacity: number) => {
      const material = new MeshBasicMaterial({
        color,
        transparent: true,
        opacity,
        side: DoubleSide,
        depthWrite: false,
      });
      // Six segments make a hexagon; turned by 30° it matches the pointy-top tiles.
      const geometry = new RingGeometry(inner, outer, 6, 1)
        .rotateZ(Math.PI / 6)
        .rotateX(-Math.PI / 2);
      return new Instances(geometry, 64, material);
    };
    this.battles = ring(0.7, 0.92, BATTLE, 0.85);
    this.scans = ring(0.55, 0.66, '#cfd8ff', 0.7);
    for (const pool of [this.battles, this.scans, this.bolts])
      pool.mesh.layers.set(LAYER_NO_OUTLINE);
    for (const pool of this.pools()) this.root.add(pool.mesh);
    this.root.add(this.overlays);
    this.hover.visible = false;
    this.picked.visible = false;
    this.root.add(this.hover, this.picked, this.names, this.progress, this.badges);
  }

  update(state: GameState) {
    this.state = state;
    let fleets: readonly FleetState[] = state.fleets;
    if (this.setFog) {
      // Fog of war (#33): only what the player's capital, fleets, outposts and allies see.
      const input = visionInput(state);
      const visible = visibleHexes(input, this.me);
      fleets = seenFleets(input, this.me, visible);
      this.visible = visible;
      const bits = state.explored[this.me];
      const grew = bits !== this.exploredFrom;
      if (grew) {
        this.exploredFrom = bits;
        this.explored = exploredKeys(state, this.me);
      }
      const signature = [...visible].join(';');
      if (signature !== this.fogSignature || grew) {
        this.fogSignature = signature;
        this.setFog(visible, this.explored);
      }
    }
    // Map objects stand only where the player has looked (#97); its own always.
    const known = (h: { q: number; r: number }, owner?: string) =>
      !this.explored || owner === this.me || this.explored.has(hexKey(h));
    this.seen = fleets;
    // Badges of fleets gone from sight (or from the match) go with them.
    const inSight = new Set(fleets.map((f) => f.id));
    for (const [id, b] of this.badgeOf) {
      if (inSight.has(id)) continue;
      this.badges.remove(b.object);
      b.object.element.remove();
      this.badgeOf.delete(id);
    }
    const live = new Set<number>();
    const slots = new Map<string, number>();
    for (const f of fleets) {
      live.add(f.id);
      const key = hexKey(f.hex);
      const slot = slots.get(key) ?? 0;
      slots.set(key, slot + 1);
      const target = fleetPosition(f, slot);
      this.targets.set(f.id, target);
      if (!this.shown.has(f.id)) this.shown.set(f.id, target.at.clone());
    }
    for (const id of [...this.shown.keys()]) {
      if (!live.has(id)) {
        this.shown.delete(id);
        this.targets.delete(id);
      }
    }

    const { tick } = state;
    if (this.bodies?.system !== state.system) {
      this.bodies = {
        system: state.system,
        byHex: new Map(state.system.planets.map((p) => [hexKey(p.hex), p])),
      };
    }
    const bodies = this.bodies.byHex;
    // Asteroids found in clouds: a few rocks each, from their id so they never jump.
    this.rocks.begin();
    for (const a of state.exploration.asteroids) {
      if (!known(a.hex)) continue;
      let seed = 0;
      for (const ch of a.id) seed = (seed * 31 + ch.charCodeAt(0)) % 9973;
      for (let i = 0; i < 4; i++) {
        const angle = seed + i * 2.1;
        const at = hexToWorld(a.hex, 0.3 + (i % 2) * 0.14).add(
          new Vector3(Math.cos(angle) * 0.38, 0, Math.sin(angle) * 0.38),
        );
        this.rocks.add(at, ASTEROID, 0.15 + ((seed + i * 7) % 5) * 0.03);
      }
    }
    this.rocks.end();
    this.derricks.begin();
    this.mines.begin();
    this.stations.begin();
    for (const o of state.outposts) {
      if (!known(o.hex, o.owner)) continue;
      const working = o.readyTick <= tick && !(o.downUntil !== undefined && o.downUntil > tick);
      const color = working ? seatColorHex(o.owner) : DOWN;
      if (o.kind === 'lagrangeStation') {
        this.stations.add(hexToWorld(o.hex, 0.45), color);
        continue;
      }
      // On top of the body it works: a found asteroid stands where its rocks are.
      const body = bodies.get(hexKey(o.hex));
      const top = body ? bodyTop(body) : 0.56;
      if (o.kind === 'gasHarvester') this.derricks.add(hexToWorld(o.hex, top - 0.04), color, 1.5);
      else this.mines.add(hexToWorld(o.hex, top - 0.04), color, 1.4);
    }
    this.derricks.end();
    this.mines.end();
    this.stations.end();

    this.debris.begin();
    for (const d of state.debris) {
      if (!known(d.hex)) continue;
      const scale = Math.min(1.8, 0.7 + d.metals / 60);
      const at = hexToWorld(d.hex, 0.25);
      this.debris.add(at.clone().add(new Vector3(-0.25, 0, 0.15)), '#8a8f9e', scale);
      this.debris.add(at.clone().add(new Vector3(0.2, 0.05, -0.2)), '#8a8f9e', scale * 0.7);
    }
    this.debris.end();

    this.swarm.begin();
    for (const m of state.megastructures) {
      if (!known(m.hex, m.owner)) continue;
      const at = hexToWorld(m.hex, 0.4);
      const stages = m.stages + (m.buildingUntil !== null ? 1 : 0);
      for (let i = 0; i < Math.min(stages, MEGASTRUCTURE.stages); i++) {
        const color = i < m.stages ? seatColorHex(m.owner) : DOWN;
        this.swarm.add(at.clone().add(new Vector3(0, i * 0.22, 0)), color, 1 - i * 0.15);
      }
    }
    this.swarm.end();

    this.updateOverlays(state);
    this.updateNames(state, known);
    this.updateProgress(state);
  }

  /** A bar over every scan and construction of the player, its label above it. */
  private updateProgress(state: GameState) {
    const items = progressItems(state, this.me, this.locale);
    const live = new Set<string>();
    const stacked = new Map<string, number>();
    for (const item of items) {
      live.add(item.id);
      let bar = this.bars.get(item.id);
      if (!bar) {
        const element = document.createElement('div');
        element.className = 'map-progress';
        const text = document.createElement('div');
        text.className = 'map-progress__label';
        const track = document.createElement('div');
        track.className = 'map-progress__track';
        const fill = document.createElement('span');
        track.append(fill);
        element.append(text, track);
        bar = { object: new CSS2DObject(element), fill, text };
        this.bars.set(item.id, bar);
        this.progress.add(bar.object);
      }
      const key = hexKey(item.hex);
      const row = stacked.get(key) ?? 0;
      stacked.set(key, row + 1);
      // Above the body on the hex; a second timer on the same hex sits a little higher.
      bar.object.position.copy(hexToWorld(item.hex, 1.15 + row * 0.55));
      bar.fill.style.width = `${Math.round(item.value * 100)}%`;
      if (bar.text.textContent !== item.label) bar.text.textContent = item.label;
    }
    for (const [id, bar] of this.bars) {
      if (live.has(id)) continue;
      this.progress.remove(bar.object);
      bar.object.element.remove();
      this.bars.delete(id);
    }
  }

  private updateNames(
    state: GameState,
    known: (h: { q: number; r: number }, owner?: string) => boolean,
  ) {
    const shown = state.outposts.filter((o) => known(o.hex, o.owner));
    const key = shown.map((o) => `${o.id}:${o.owner}`).join('|');
    if (key === this.namesKey) return;
    this.namesKey = key;
    for (const child of [...this.names.children]) {
      this.names.remove(child);
      if (child instanceof CSS2DObject) child.element.remove();
    }
    for (const o of shown) {
      const name = label(outpostName(this.locale, o), 'map-label map-label--quiet');
      const body = this.bodies?.byHex.get(hexKey(o.hex));
      name.position.copy(hexToWorld(o.hex, (body ? bodyTop(body) : 0.56) + 1.4));
      this.names.add(name);
    }
  }

  /** Where the picked ship would fly (#162): a faint dashed line, under the cursor's hex. */
  private preview: Line | null = null;
  private previewKey = '';

  setPreview(hexes: readonly Hex[] | null) {
    const key = hexes && hexes.length > 1 ? hexes.map(hexKey).join(';') : '';
    if (key === this.previewKey) return;
    this.previewKey = key;
    if (this.preview) {
      this.preview.removeFromParent();
      this.preview.geometry.dispose();
      (this.preview.material as LineDashedMaterial).dispose();
      this.preview = null;
    }
    if (!hexes || hexes.length < 2) return;
    const geometry = new BufferGeometry().setFromPoints(hexes.map((h) => hexToWorld(h, 0.14)));
    this.preview = new Line(
      geometry,
      new LineDashedMaterial({
        color: 0xe6eaf5,
        dashSize: 0.22,
        gapSize: 0.12,
        transparent: true,
        opacity: 0.9,
      }),
    );
    this.preview.computeLineDistances();
    this.preview.layers.set(LAYER_NO_OUTLINE);
    this.root.add(this.preview);
  }

  /** The selected fleet's remaining path: a dashed line from where it is drawn to its goal. */
  private updateRoute(state: GameState) {
    const fleet = state.fleets.find((f) => f.id === this.selectedFleet);
    const shown = fleet && this.shown.get(fleet.id);
    if (fleet && shown) this.picked.position.copy(hexToWorld(fleet.hex));
    const key =
      fleet && fleet.path.length > 0 ? `${fleet.id}:${fleet.path.map(hexKey).join(';')}` : '';
    if (key !== this.routeKey) {
      this.routeKey = key;
      if (this.route) {
        this.route.removeFromParent();
        this.route.geometry.dispose();
        (this.route.material as LineDashedMaterial).dispose();
        this.route = null;
      }
      if (fleet && fleet.path.length > 0) {
        const points = [hexToWorld(fleet.hex, 0.12), ...fleet.path.map((h) => hexToWorld(h, 0.12))];
        const geometry = new BufferGeometry().setFromPoints(points);
        this.route = new Line(
          geometry,
          new LineDashedMaterial({
            color: seatColorHex(fleet.owner),
            dashSize: 0.25,
            gapSize: 0.18,
          }),
        );
        this.route.computeLineDistances();
        this.route.layers.set(LAYER_NO_OUTLINE);
        this.root.add(this.route);
      }
    }
  }

  /** Every frame: fleets glide, battle rings pulse. */
  /** Hex under the cursor, or null. */
  setHover(h: Hex | null) {
    this.hover.visible = h !== null;
    if (h) this.hover.position.copy(hexToWorld(h));
  }

  /** Fleets picked together with a drag (#111): their badges light up. */
  setGroup(ids: readonly number[]) {
    this.group = new Set(ids);
    for (const [id, b] of this.badgeOf) {
      b.object.element.classList.toggle('fleet-badge--picked', this.group.has(id));
    }
  }

  setHit(ids: ReadonlySet<number>) {
    this.hit = ids;
    for (const [id, b] of this.badgeOf) {
      b.object.element.classList.toggle('fleet-badge--hit', this.hit.has(id));
    }
  }

  /** A fleet's badge, made once and moved with it; its numbers change only when they do. */
  private badge(f: FleetState, at: Vector3, color: string) {
    let b = this.badgeOf.get(f.id);
    if (!b) {
      const element = document.createElement('div');
      element.className = 'fleet-badge';
      element.innerHTML = '<b></b><span><i></i></span>';
      const object = new CSS2DObject(element);
      b = {
        object,
        count: element.querySelector('b')!,
        hull: element.querySelector('i')!,
        key: '',
      };
      this.badgeOf.set(f.id, b);
      this.badges.add(object);
      element.classList.toggle('fleet-badge--picked', this.group.has(f.id));
      element.classList.toggle('fleet-badge--hit', this.hit.has(f.id));
    }
    b.object.position.set(at.x, at.y + 0.85, at.z);
    const hull = Math.round(hullShare(f) * 100);
    const key = `${f.ships.length}|${hull}|${color}`;
    if (key !== b.key) {
      b.key = key;
      b.count.textContent = String(f.ships.length);
      b.hull.style.width = `${hull}%`;
      b.object.element.style.setProperty('--seat', color);
    }
  }

  /** The selected hex (a fleet's or any other) and the fleet whose route to show. */
  setSelection(h: Hex | null, fleetId: number | null) {
    this.picked.visible = h !== null;
    if (h) this.picked.position.copy(hexToWorld(h));
    this.selectedFleet = fleetId;
  }

  /**
   * A round of fire (#117): bolts by class — a corvette's quick dart, a
   * frigate's pair, a cruiser's heavy charge — only where the player sees.
   */
  fire(shots: readonly { from: Hex; to: Hex; owner: string; cls: ShipClassId }[]) {
    for (const s of shots) {
      const seen =
        !this.visible || this.visible.has(hexKey(s.from)) || this.visible.has(hexKey(s.to));
      if (!seen) continue;
      const color = s.owner === 'pirates' ? PIRATE_COLOR : seatColorHex(s.owner);
      // A little spread, so a close fight still shows bolts crossing the hex.
      const k = this.flying.length;
      const jitter = (n: number) =>
        new Vector3(Math.sin(k * 2.3 + n) * 0.25, 0, Math.cos(k * 1.7 + n) * 0.25);
      const from = hexToWorld(s.from, 0.35).add(jitter(0));
      const to = hexToWorld(s.to, 0.35).add(jitter(1));
      const bolt = BOLTS[s.cls] ?? { count: 1, size: 0.8 };
      for (let i = 0; i < bolt.count; i++) {
        if (this.flying.length >= 500) break;
        this.flying.push({ from, to, start: this.now + i * 0.12, color, size: bolt.size });
      }
    }
  }

  private drawBolts(seconds: number) {
    this.bolts.begin();
    const at = new Vector3();
    const dir = new Vector3();
    const turn = new Quaternion();
    this.flying = this.flying.filter((b) => seconds - b.start < BOLT_SECONDS);
    for (const b of this.flying) {
      const t = (seconds - b.start) / BOLT_SECONDS;
      if (t < 0) continue;
      at.copy(b.from).lerp(b.to, t);
      dir.copy(b.to).sub(b.from);
      if (dir.lengthSq() < 1e-6) dir.set(0, 0, 1);
      turn.setFromUnitVectors(FORWARD, dir.normalize());
      this.bolts.add(at, b.color, b.size, turn);
    }
    this.bolts.end();
  }

  frame(dt: number, seconds: number, cameraDistance = 0) {
    this.now = seconds;
    this.drawBolts(seconds);
    const state = this.state;
    if (!state) return;
    const follow = 1 - Math.exp(-dt * 10);
    for (const pool of Object.values(this.hulls)) pool.begin();
    this.explorers.begin();
    this.scans.begin();
    const turn = new Quaternion();
    const badgesOn = cameraDistance > 0 && cameraDistance < BADGES_DISTANCE;
    this.badges.visible = badgesOn;
    for (const f of this.seen) {
      const target = this.targets.get(f.id);
      const shown = this.shown.get(f.id);
      if (!target || !shown) continue;
      shown.lerp(target.at, follow);
      const color = f.owner === 'pirates' ? PIRATE_COLOR : seatColorHex(f.owner);
      turn.setFromAxisAngle(UP, target.heading ?? 0);
      const size = 0.85 + Math.min(f.ships.length, 12) * 0.05;
      const flagship = FLAGSHIP_ORDER.find((c) => f.ships.some((s) => s.cls === c));
      if (!flagship) {
        turn.setFromAxisAngle(UP, seconds * 0.8);
        this.explorers.add(shown, color, 1, turn);
      } else this.hulls[flagship].add(shown, color, size, turn);
      if (f.scan)
        this.scans.add(hexToWorld(f.hex, 0.06), '#cfd8ff', 1 + 0.08 * Math.sin(seconds * 4));
      if (badgesOn) this.badge(f, shown, color);
    }
    for (const pool of Object.values(this.hulls)) pool.end();
    this.explorers.end();
    this.scans.end();

    this.battles.begin();
    const pulse = 1 + 0.06 * Math.sin(seconds * 6);
    for (const key of state.battles) {
      if (this.visible && !this.visible.has(key)) continue;
      const [q, r] = key.split(',').map(Number);
      this.battles.add(hexToWorld({ q: q!, r: r! }, 0.05), BATTLE, pulse);
    }
    for (const s of state.sieges) {
      const capital = state.system.planets.find((p) => p.homeOf === s.victim);
      if (capital && this.visible && !this.visible.has(hexKey(capital.hex))) continue;
      if (capital) this.battles.add(hexToWorld(capital.hex, 0.05), BATTLE, pulse * 1.15);
    }
    this.battles.end();

    this.updateRoute(state);
    // Close up, outposts and stations carry names; from afar only capitals do.
    this.names.visible = cameraDistance > 0 && cameraDistance < NAMES_DISTANCE;

    for (const child of this.overlays.children) {
      if (child.userData.countdown && child instanceof CSS2DObject) {
        const left = clock(Math.max(0, child.userData.countdown - state.tick));
        child.element.textContent = `${child.userData.title} · ${left}`;
      }
    }
  }

  /** The crisis sector, project sites and their labels; rebuilt only when they change. */
  private updateOverlays(state: GameState) {
    const crises = state.crises.filter((c) => c.warned.includes(this.me));
    const key = JSON.stringify([
      crises.map((c) => [c.id, c.sector]),
      state.projects.map((p) => [p.id, hexKey(p.hex)]),
      this.locale,
    ]);
    if (key === this.overlayKey) return;
    this.overlayKey = key;
    this.clearOverlays();

    // The struck tiles take the star's heat; the countdown and the crisis name sit on
    // the sector's middle line, a third of the way out. One crisis at a time is shown:
    // the director never plans two at once.
    const crisis = crises[0];
    this.tintSector(crisis ? crisis.sector : undefined);
    if (crisis) {
      const outer = this.mapRadius * HEX_SIZE * Math.sqrt(3);
      const angle = crisis.sector === null ? -Math.PI / 2 : (-60 * crisis.sector * Math.PI) / 180;
      const timer = label('', 'map-label map-label--heat');
      timer.position.set(Math.cos(angle) * outer * 0.35, 0.4, Math.sin(angle) * outer * 0.35);
      timer.userData.countdown = crisis.strikeAt;
      timer.userData.title = t(this.locale, `crisis.${crisis.kind}.name`);
      this.overlays.add(timer);
    }
    for (const p of state.projects) {
      this.overlays.add(hexOutline(p.hex, HEAT));
      const name = label(t(this.locale, `project.${p.kind}.name`), 'map-label map-label--heat');
      name.position.copy(hexToWorld(p.hex, 1.1));
      this.overlays.add(name);
    }
  }

  private clearOverlays() {
    for (const child of [...this.overlays.children]) {
      this.overlays.remove(child);
      if (child instanceof CSS2DObject) child.element.remove();
      if (child instanceof Mesh || child instanceof LineLoop) {
        child.geometry.dispose();
        (child.material as MeshBasicMaterial).dispose();
      }
    }
  }

  private pools(): Instances[] {
    return [
      ...Object.values(this.hulls),
      this.bolts,
      this.explorers,
      this.derricks,
      this.mines,
      this.rocks,
      this.stations,
      this.debris,
      this.battles,
      this.scans,
      this.swarm,
    ];
  }

  dispose() {
    this.tintSector(undefined);
    this.clearOverlays();
    for (const child of this.names.children) {
      if (child instanceof CSS2DObject) child.element.remove();
    }
    for (const bar of this.bars.values()) bar.object.element.remove();
    for (const b of this.badgeOf.values()) b.object.element.remove();
    this.badgeOf.clear();
    this.bars.clear();
    for (const pool of this.pools()) pool.dispose();
    this.root.removeFromParent();
  }
}
